import { useState } from 'react';
import * as XLSX from 'xlsx';
import { Button } from '../ui/Button';
import toast from 'react-hot-toast';

interface ExcelImporterProps {
  onDataImported: (data: any[]) => Promise<void>;
  templateFields: string[];
  entityName: string;
}

export const ExcelImporter = ({ onDataImported, templateFields, entityName }: ExcelImporterProps) => {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Por favor, selecciona un archivo Excel');
      return;
    }

    setLoading(true);

    try {
      const data = await readExcelFile(file);
      
      // Validar que los datos tengan las columnas necesarias
      const firstRow = data[0];
      const missingFields = templateFields.filter(field => !Object.keys(firstRow).includes(field));
      
      if (missingFields.length > 0) {
        toast.error(`El archivo no contiene los campos requeridos: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }

      await onDataImported(data);
      toast.success(`${data.length} ${entityName} importados correctamente`);
      setFile(null);
      
      // Limpiar el input file
      const fileInput = document.getElementById('excel-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
    } catch (error: any) {
      console.error('Error al importar datos:', error);
      toast.error(error.message || `Error al importar ${entityName}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    // Crear un objeto de datos de ejemplo
    const sampleData = [
      templateFields.reduce((obj, field) => {
        obj[field] = ''; // Valores vacíos para la plantilla
        return obj;
      }, {} as Record<string, string>)
    ];

    // Crear un libro de trabajo
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(sampleData);
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');

    // Descargar el archivo
    XLSX.writeFile(wb, `plantilla_${entityName}.xlsx`);
  };

  const readExcelFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet);
          
          resolve(json);
        } catch (error) {
          reject(new Error('Error al procesar el archivo Excel'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error al leer el archivo'));
      };
      
      reader.readAsBinaryString(file);
    });
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-white">
      <h3 className="text-lg font-medium">Importar {entityName} desde Excel</h3>
      
      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
        <div className="flex-grow">
          <input
            id="excel-file"
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100"
          />
        </div>
        
        <div className="flex space-x-2">
          <Button
            type="button"
            onClick={handleDownloadTemplate}
            variant="outline"
            size="sm"
          >
            Descargar Plantilla
          </Button>
          
          <Button
            type="button"
            onClick={handleImport}
            loading={loading}
            disabled={!file}
            size="sm"
          >
            Importar
          </Button>
        </div>
      </div>
      
      {file && (
        <div className="text-sm text-gray-500">
          Archivo seleccionado: <span className="font-medium">{file.name}</span>
        </div>
      )}
      
      <div className="text-xs text-gray-500 mt-2">
        <p>Instrucciones:</p>
        <ol className="list-decimal list-inside mt-1 space-y-1">
          <li>Descarga la plantilla de Excel</li>
          <li>Completa los datos en la plantilla</li>
          <li>Guarda el archivo y súbelo aquí</li>
          <li>Haz clic en "Importar" para cargar los datos</li>
        </ol>
      </div>
    </div>
  );
};
